import { Injectable, OnDestroy } from '@angular/core';
import * as speechSdk from "microsoft-cognitiveservices-speech-sdk";
import { Subject } from "rxjs";
import { Config, RecordingToolTip, SpeechEvents } from "../../models/speech-to-text.interface";
import { CVA_CONSTANTS, MicrophoneErrorMessages } from 'app/util/constants';
import { CVANotificationService } from '../cva-notification/cva-notification.service';
import { NotificationClassEnum } from 'app/models/notification-class.enum';
import { SessionInactivityService } from '../session-inactivity/session-inactivity.service';
import { AudioStreamService } from '../audio-service/audio-stream.service';


@Injectable({
  providedIn: 'root'
})
export class SpeechToTextService implements OnDestroy {
  //Regex to find last . from sentense
  private readonly findLastDot = /\.$/;
  private _isWakeLockSupported: boolean = 'wakeLock' in navigator;

  //to control full stop of sentense while recognizing
  public removeLastFullStop = false;

  //to send regcognized text to outside of component
  recognizer: Subject<string> = new Subject();
  recognizing: Subject<string> = new Subject();

  speechEvent: Subject<SpeechEvents> = new Subject();

  //local variables
  speechConfig: speechSdk.SpeechConfig;
  audioConfig: speechSdk.AudioConfig;
  speechRecognizer: speechSdk.SpeechRecognizer;

  //local variable to check if full stop word triggered or not
  isFullStopCaptured: boolean = false;
  //configurations required to inti the object
  static configurations: Config;

  wakeLock = null;

  private isRecording = false;
  private currentMediaStream: MediaStream | null = null;
  private selectedDeviceId: string | null = null;
  private transcribeStream: MediaStream | null = null;


  readonly showMicIconStatuses = [
    RecordingToolTip.START_RECORDING,
    RecordingToolTip.INITIALIZING,
    RecordingToolTip.RESUME
  ];

  readonly showLabelStatuses = [
    RecordingToolTip.START_RECORDING,
    RecordingToolTip.STOP_RECORDING,
    RecordingToolTip.RESUME
  ];

  readonly disabledStatuses = [
    RecordingToolTip.INITIALIZING,
    RecordingToolTip.PROCESSING
  ];

  constructor(
    private readonly cvaNotificationService: CVANotificationService,
    private readonly sessionInactivityService: SessionInactivityService,
    private readonly audioStreamService: AudioStreamService
  ) {
    this.speechConfig = speechSdk.SpeechConfig.fromSubscription(SpeechToTextService.configurations.apiKey, SpeechToTextService.configurations.region);
    this.speechConfig.endpointId = SpeechToTextService.configurations.endpointId;
    this.speechConfig.speechRecognitionLanguage = SpeechToTextService.configurations.speechRecognitionLanguage;
    this.speechConfig.enableDictation();
    this.audioConfig = speechSdk.AudioConfig.fromDefaultMicrophoneInput();
    this.speechRecognizer = new speechSdk.SpeechRecognizer(
      this.speechConfig,
      this.audioConfig,
    );
    this.addEvents();
  }

  releaseMicrophone(): void {
    this.stopTranscribeStream();
  }

  async setSelectedInputDevice(deviceId: string, isManualChange: boolean = false): Promise<void> {
    if (this.selectedDeviceId !== deviceId && !this.isRecording) {
      this.stopTranscribeStream();
    }
    // Simply store the selected device ID
    // The actual device change will be applied when recording starts
    this.selectedDeviceId = deviceId;

    // Only show snackbar on manual user selection, not during initialization
    if (isManualChange) {
      this.cvaNotificationService.showNotification(
        'Microphone device selected. Changes will apply on next recording.',
        NotificationClassEnum.SUCCESS
      );
    }
  }

  setMediaStream(stream: MediaStream | null) {
    try {
      this.currentMediaStream = stream;

      // Close existing recognizer if present
      if (this.speechRecognizer) {
        this.speechRecognizer.close();
      }

      if (stream) {
        // Validate stream has audio tracks
        const audioTracks = stream.getAudioTracks();
        if (audioTracks.length === 0) {
          console.error('Provided stream has no audio tracks');
          this.audioConfig = speechSdk.AudioConfig.fromDefaultMicrophoneInput();
        } else {
          // Use the provided media stream
          this.audioConfig = speechSdk.AudioConfig.fromStreamInput(stream);
        }
      } else {
        // Fall back to default microphone
        this.audioConfig = speechSdk.AudioConfig.fromDefaultMicrophoneInput();
      }

      // Create new recognizer with updated audio config
      this.speechRecognizer = new speechSdk.SpeechRecognizer(this.speechConfig, this.audioConfig);
      this.addEvents();
    } catch (error) {
      console.error('Error setting media stream:', error);
      // Fallback to default microphone on error
      this.audioConfig = speechSdk.AudioConfig.fromDefaultMicrophoneInput();
      this.speechRecognizer = new speechSdk.SpeechRecognizer(this.speechConfig, this.audioConfig);
      this.addEvents();
    }
  }

  get isWakeLockSupported(): boolean {
    return this._isWakeLockSupported;
  }

  set isWakeLockSupported(value: boolean) {
    this._isWakeLockSupported = value;
  }

  //set initial configurations
  public static init(config: Config) {
    this.configurations = config;
  }


  //to add basic event for speech to text recognition
  addEvents() {
    this.speechRecognizer.recognizing = (_s, event) => {
      this.recognizing.next(event.result.text);
      this.processFullStop(event.result.text);
    };

    this.speechRecognizer.recognized = (_s, event) => {
      if (event.result.reason === speechSdk.ResultReason.RecognizedSpeech && event.result.text) {
        let recognizedText = event.result.text;
        if (this.removeLastFullStop) {
          recognizedText = this.removeFullStop(recognizedText);
        }
        this.recognizer.next(recognizedText);
      } else if (!event.result.text && this.isFullStopCaptured) {
        this.recognizer.next('.');
      }
    };
  }

  processFullStop(text: string): void {
    if (["full stop", "period"].indexOf(text) > -1) {
      this.isFullStopCaptured = true;
    } else {
      this.isFullStopCaptured = false;
    }
  }

  removeFullStop(text: string): string {
    return text.replace(this.findLastDot, "");
  }

  //to start the recording
  async startRecording() {
    try {
      await this.applySelectedDevice();
      this.logWakeLockSupport();
      return this.startRecognition();
    } catch (error) {
      console.error('Error starting recording with selected device:', error);
      this.cvaNotificationService.showNotification(
        'Failed to start recording. Please try again.',
        NotificationClassEnum.ERROR
      );
      throw error;
    }
  }

  private async applySelectedDevice(): Promise<void> {
    if (!this.selectedDeviceId) {
      this.stopTranscribeStream();
      return;
    }

    if (this.audioStreamService.audioSource === 'telehealth') {
      this.stopTranscribeStream();
      await this.applyTelehealthDevice();
    } else {
      await this.applyTranscribeDevice();
    }
  }

  private async applyTelehealthDevice(): Promise<void> {
    const newMixedStream = await this.audioStreamService.updateTelehealthMicrophoneDevice(this.selectedDeviceId);
    if (newMixedStream) {
      this.setMediaStream(newMixedStream);
    }
  }

  private async applyTranscribeDevice(): Promise<void> {
    let selectedStream: MediaStream | null = null;
    try {
      // Get the media stream first BEFORE closing recognizer
      selectedStream = await navigator.mediaDevices.getUserMedia({
        audio: { deviceId: { exact: this.selectedDeviceId } }
      });

      const actualId = selectedStream.getAudioTracks()[0]?.getSettings().deviceId;
      console.log('[Mic] Requested:', this.selectedDeviceId, 'Actual:', actualId);
      if (actualId !== this.selectedDeviceId) {
        throw new Error(`Requested microphone ${this.selectedDeviceId}, got ${actualId}`);
      }

      // Create new recognizer BEFORE closing the old one
      const newAudioConfig = speechSdk.AudioConfig.fromStreamInput(selectedStream);
      const newRecognizer = new speechSdk.SpeechRecognizer(this.speechConfig, newAudioConfig);

      // Only now close the old recognizer
      if (this.speechRecognizer) {
        this.speechRecognizer.close();
      }
      this.stopTranscribeStream();
      this.transcribeStream = selectedStream;

      this.audioConfig = newAudioConfig;
      this.speechRecognizer = newRecognizer;
      this.addEvents();
    } catch (deviceError) {
      console.error('[Mic] Failed to apply microphone:', deviceError);
      selectedStream?.getTracks().forEach(t => t.stop());
      this.fallbackToDefaultMicrophone(deviceError);
    }
  }

  private fallbackToDefaultMicrophone(deviceError: Error): void {
    this.stopTranscribeStream();
    this.cvaNotificationService.showNotification(
      'Selected microphone unavailable. Using default microphone.',
      NotificationClassEnum.ERROR
    );
    console.warn('Failed to access selected device, falling back to default microphone:', deviceError);
    if (this.speechRecognizer) {
      this.speechRecognizer.close();
    }
    this.audioConfig = speechSdk.AudioConfig.fromDefaultMicrophoneInput();
    this.speechRecognizer = new speechSdk.SpeechRecognizer(this.speechConfig, this.audioConfig);
    this.addEvents();
  }

  private logWakeLockSupport(): void {
    if (!this.isWakeLockSupported) {
      console.log('Screen Wake Lock is not supported in your browser.');
    }
  }

  private startRecognition(): Promise<boolean> {
    return new Promise((resolve, reject) => {
      this.speechRecognizer.startContinuousRecognitionAsync(
        async () => {
          this.isRecording = true;
          this.sessionInactivityService.setSpeechInProgress(true);
          await this.tryAcquireWakeLock();
          resolve(true);
        },
        (err) => {
          this.sessionInactivityService.setSpeechInProgress(false);
          const error = this.getErrorMessage(err);
          reject(new Error(error));
        },
      );
    });
  }

  async tryAcquireWakeLock() {
    if (!this.isWakeLockSupported || document.visibilityState !== 'visible') {
      console.warn('Cannot acquire Wake Lock');
      return;
    }

    try {
      this.wakeLock = await navigator.wakeLock.request('screen');
      console.log('Screen Wake Lock acquired.');
    } catch (err) {
      this.handleWakeLockError(err);
    }
  }



  handleWakeLockError(err) {
    switch (err.name) {
      case 'AbortError':
        console.warn('Wake Lock aborted: possibly due to power-saving mode or low battery.');
        this.cvaNotificationService.showNotification(
          CVA_CONSTANTS.MESSAGE.WAKE_LOCK_ABORTED,
          NotificationClassEnum.ERROR
        );
        break;
      default:
        console.error(`Wake Lock error (${err.name}): ${err.message}`);
    }
  }

  //to stop recording
  async stopRecording() {
    if (this.wakeLock) {
      await this.wakeLock.release();
      console.log('Screen Wake Lock released.');
      this.wakeLock = null;
    }
    return new Promise((resolve, reject) => {
      this.speechRecognizer.stopContinuousRecognitionAsync(() => {
        this.isRecording = false;

        this.sessionInactivityService.setSpeechInProgress(false);
        resolve(true);
      }, (err) => {
        const error = this.getErrorMessage(err);
        reject(new Error(error));
      },
      );
    });
  }

  //one time use of speech to text
  recognizeOnce(): Promise<string> {
    return new Promise((resolve, reject) => {
      this.speechRecognizer.recognizeOnceAsync(result => {
        if (result?.text) {
          resolve(result.text);
        } else {
          reject(result instanceof Error ? result : new Error(result?.errorDetails));
        }
      });
    });
  }

  getErrorMessage(error: string): string {
    const errorName = error.split(':')[1]?.trim() || '';

    switch (errorName) {
      case 'NotAllowedError':
        return MicrophoneErrorMessages.NotAllowedError;
      case 'NotFoundError':
        return MicrophoneErrorMessages.NotFoundError;
      case 'NotReadableError':
        return MicrophoneErrorMessages.NotReadableError;
      case 'NotSupportedError':
        return MicrophoneErrorMessages.NotSupportedError;
      case 'SecurityError':
        return MicrophoneErrorMessages.SecurityError;
      case 'AbortError':
        return MicrophoneErrorMessages.AbortError;
      case 'OverconstrainedError':
        return MicrophoneErrorMessages.OverconstrainedError;
      case 'InvalidStateError':
        return MicrophoneErrorMessages.InvalidStateError;
      case 'TypeError':
        return MicrophoneErrorMessages.TypeError;
      default:
        return MicrophoneErrorMessages.UnknownError;
    }
  }

  extractErrorMessage(e: unknown): string {
    if (e instanceof Error) {
      return e.message;
    }

    if (typeof e === 'string') {
      return e;
    }

    if (typeof e === 'object' && e !== null) {
      // Try known patterns (e.g., HTTP error response)
      if ('error' in e && typeof e.error === 'string') return e.error;
      if ('message' in e && typeof e.message === 'string') return e.message;
    }

    return 'An unknown error occurred.';
  }

  private stopTranscribeStream(): void {
    this.transcribeStream?.getTracks().forEach(t => t.stop());
    this.transcribeStream = null;
  }

  ngOnDestroy(): void {
    this.stopTranscribeStream();
  }
}
